import React, { useState } from 'react';
import { BookOpen, Check, X, FileText, Lightbulb, Wrench } from 'lucide-react';
import type { CreateDiarioClasseInput } from '../../api/hooks/useDiarioClasse';
import { formatDateBR } from '../../utils/dateUtils';

/**
 * Modal de registro do Diário de Classe (pós-chamada).
 *
 * Aparece após o professor concluir uma chamada.
 * Campos: conteúdo ministrado, metodologia, recursos, observações, objetivos cumpridos.
 *
 * @param chamadaId - ID da chamada concluída
 * @param professorId - ID do professor
 * @param turmaId - ID da turma
 * @param materiaId - ID da matéria
 * @param turmaNome - Nome da turma (display)
 * @param materiaNome - Nome da matéria (display)
 * @param dataReferencia - Data da aula
 * @param onSalvar - Callback ao salvar o diário
 * @param onPular - Callback ao pular o registro
 */
interface DiarioClasseModalProps {
  chamadaId: string;
  professorId: string;
  turmaId: string;
  materiaId: string;
  turmaNome: string;
  materiaNome: string;
  dataReferencia: string;
  onSalvar: (dados: CreateDiarioClasseInput) => void;
  onPular: () => void;
  salvando?: boolean;
}

export const DiarioClasseModal: React.FC<DiarioClasseModalProps> = ({
  chamadaId,
  professorId,
  turmaId,
  materiaId,
  turmaNome,
  materiaNome,
  dataReferencia,
  onSalvar,
  onPular,
  salvando = false,
}) => {
  const [conteudo, setConteudo] = useState('');
  const [metodologia, setMetodologia] = useState('');
  const [recursos, setRecursos] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [objetivosCumpridos, setObjetivosCumpridos] = useState(true);

  const handleSalvar = () => {
    if (!conteudo.trim()) return;
    onSalvar({
      chamadaId,
      professorId,
      turmaId,
      materiaId,
      dataReferencia,
      conteudoMinistrado: conteudo.trim(),
      metodologia: metodologia.trim() || undefined,
      recursosUtilizados: recursos.trim() || undefined,
      observacoes: observacoes.trim() || undefined,
      objetivosCumpridos,
    });
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>
            <BookOpen size={18} style={{ color: '#2563eb' }} />
          </div>
          <div>
            <h2 style={styles.title}>Diário de Classe</h2>
            <p style={styles.subtitle}>
              {turmaNome} · {materiaNome} · {formatDateBR(dataReferencia)}
            </p>
          </div>
          <button onClick={onPular} style={styles.closeBtn} title="Pular registro">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {/* Conteúdo Ministrado (obrigatório) */}
          <div style={styles.field}>
            <label style={styles.label}>
              <FileText size={14} style={{ marginRight: 6 }} />
              Conteúdo Ministrado *
            </label>
            <textarea
              value={conteudo}
              onChange={(e) => setConteudo(e.target.value)}
              placeholder="O que foi ensinado nesta aula?"
              style={styles.textarea}
              rows={3}
              autoFocus
            />
          </div>

          {/* Metodologia */}
          <div style={styles.field}>
            <label style={styles.label}>
              <Lightbulb size={14} style={{ marginRight: 6 }} />
              Metodologia
            </label>
            <input
              type="text"
              value={metodologia}
              onChange={(e) => setMetodologia(e.target.value)}
              placeholder="Ex: Aula expositiva + exercícios práticos"
              style={styles.input}
            />
          </div>

          {/* Recursos */}
          <div style={styles.field}>
            <label style={styles.label}>
              <Wrench size={14} style={{ marginRight: 6 }} />
              Recursos Utilizados
            </label>
            <input
              type="text"
              value={recursos}
              onChange={(e) => setRecursos(e.target.value)}
              placeholder="Ex: Quadro branco, projetor, livro didático"
              style={styles.input}
            />
          </div>

          {/* Observações */}
          <div style={styles.field}>
            <label style={styles.label}>Observações</label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Notas adicionais sobre a aula..."
              style={styles.textarea}
              rows={2}
            />
          </div>

          {/* Objetivos Cumpridos */}
          <div
            style={styles.checkboxRow}
            onClick={() => setObjetivosCumpridos(!objetivosCumpridos)}
          >
            <div
              style={{
                ...styles.checkbox,
                ...(objetivosCumpridos ? styles.checkboxActive : {}),
              }}
            >
              {objetivosCumpridos && <Check size={14} style={{ color: '#fff' }} />}
            </div>
            <span style={styles.checkboxLabel}>Objetivos da aula foram cumpridos</span>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={onPular} style={styles.btnSecondary}>
            Pular
          </button>
          <button
            onClick={handleSalvar}
            disabled={!conteudo.trim() || salvando}
            style={{
              ...styles.btnPrimary,
              ...(!conteudo.trim() || salvando ? styles.btnDisabled : {}),
            }}
          >
            {salvando ? 'Salvando...' : 'Registrar Diário'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: 'var(--bg-card, #fff)',
    borderRadius: 16,
    width: '100%',
    maxWidth: 480,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    margin: '0 12px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 16px 12px',
    borderBottom: '1px solid var(--border-color, #e5e7eb)',
  },
  headerIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    background: 'rgba(37,99,235,0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: '0.92rem',
    fontWeight: 700,
    color: 'var(--text-main, #111)',
  },
  subtitle: {
    margin: 0,
    fontSize: '0.68rem',
    color: 'var(--text-muted, #666)',
  },
  closeBtn: {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-muted, #999)',
    padding: 4,
  },
  body: {
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.72rem',
    fontWeight: 600,
    color: 'var(--text-muted, #555)',
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--bg-card, #fff)',
    color: 'var(--text-main, #111)',
    fontSize: '0.78rem',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'var(--bg-card, #fff)',
    color: 'var(--text-main, #111)',
    fontSize: '0.78rem',
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    padding: '4px 0',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 5,
    border: '2px solid var(--border-color, #d1d5db)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    flexShrink: 0,
  },
  checkboxActive: {
    background: '#2563eb',
    borderColor: '#2563eb',
  },
  checkboxLabel: {
    fontSize: '0.75rem',
    color: 'var(--text-main, #333)',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '12px 16px 16px',
    borderTop: '1px solid var(--border-color, #e5e7eb)',
  },
  btnSecondary: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid var(--border-color, #d1d5db)',
    background: 'transparent',
    color: 'var(--text-muted, #666)',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnPrimary: {
    padding: '8px 20px',
    borderRadius: 8,
    border: 'none',
    background: 'linear-gradient(135deg, #2563eb, #1e3a5f)',
    color: '#fff',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};
